import React, { useEffect, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { Form, Button, ConfigProvider, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import AIChat from 'components/AIChat';
import { CALENDLY_LINK } from 'constants/common';
import { EAIChatPageId } from 'types/ai';
import ProviderStore from 'store/Provider';
import { PATH } from 'constants/path';
import { trackHeapEvent } from 'utils/analytics';
import useCredentialInfo from 'hooks/credentialing/useCredentialInfo';
import { ENPIType, ECredentialingFlowMode } from 'types/credentialing';
import useLoadBasicData from 'hooks/useLoadBasicData';
import {
    saveCredBillingNpiType,
    switchCredBillingType,
    getCredentialingOverview,
    getCredPractice,
} from 'api/credentialing';
import type { TCredentialingOverview } from 'types/credentialing';
import { STEPS } from './constants';
import { renderCredStep, CRED_STEP_KEYS, type CredStepKey } from './steps';
import { CredTargetProviderContext } from './steps/targetProvider';
import {
    REVIEW_SECTIONS,
    getReviewErrors,
    getSectionErrors,
    getSectionMissingFields,
    getLicensesStepValidation,
    fieldMarkableErrors,
} from './reviewSpec';
import ReviewSection from './components/ReviewSection';
import ProviderPayerModal from './components/ProviderPayerModal';
import GroupReviewView from './components/GroupReviewView';
import BillingTypePanel from './components/BillingTypePanel';
import { npiIssue, npiIssueText } from 'utils/npiCheck';
import { hydrateDates, STEP_API, stepDtoToPatch, saveStepValues } from './credReviewData';
import s from './s.module.less';
import CredentialingModal from 'components/CredentialingModal';

const BannerIcon = () => (
    <svg viewBox="0 0 20 20" fill="currentColor" width="18" height="18" aria-hidden>
        <path
            fillRule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z"
            clipRule="evenodd"
        />
    </svg>
);

// Credentialing wizard (#start) —— 结构/文案移植自设计原型 credentialing tab
// TODO(接口待提供): 提交/文件上传/草稿保存接口待后端提供；当前用 antd Form 本地 state
type TCredRole = 'solo' | 'group';

// 后端 onboardingStep 是自由字符串（provider 个人流程无严格枚举），按关键字容错映射到向导步骤下标。
// 顺序敏感：MALPRACTICE 含 "PRACTICE"，须先判定；未知/空/INIT 落到第 0 步。
const resumeStepFromToken = (token?: string): number => {
    const t = (token || '').toUpperCase();
    if (!t || t === 'INIT') return 0;
    if (t.includes('MALPRACTICE')) return 3;
    if (t.includes('LICENSE')) return 1;
    if (t.includes('PRACTICE')) return 2;
    if (t.includes('BACKGROUND') || t.includes('EXPERIENCE') || t.includes('EDUCATION')) return 4;
    if (t.includes('REFERENCE')) return 5;
    if (t.includes('VERIFICATION') || t.includes('FINAL') || t.includes('REVIEW') || t.includes('CONFIRM')) return 6;
    return 0;
};

// group 里 practice 级、由前端从 owner 预填的 Practice 步字段
const PRACTICE_SHARED_FIELDS = ['startDate', 'monthlyVisits', 'patientPayerMix'];

export default function PPACredentialingStartPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [form] = Form.useForm();
    const [getUser] = ProviderStore.useStore();
    // 本页是全屏独立向导（不挂在 PPALayout 下，避免带出侧边导航），
    // 与 PPAOnboardingSteps / NewUniProfile 等一致：由页面自身拉基础枚举数据，
    // 否则 Credentials 等依赖 setting 枚举的下拉会「No data」
    const [loadBasicData] = useLoadBasicData();

    // group owner 从 add-provider 步骤代填某个 provider 时，query 会带 providerId。
    // 带 providerId 时：跳过入口 solo/group 选择，直接进表单；所有向导接口透传该 id 作为
    // OVERRIDE-PROVIDER-ID（由 Request 决定是否真正下发 header，目标为自己时会跳过）。
    const overrideProviderId = searchParams.get('providerId') || undefined;
    const isOverrideMode = !!overrideProviderId;
    // group credentialing「Continue to review」跨页进来时带 ?mode=group-review：渲染独立的 group review，
    // 不牵动单 provider 的入口/向导/代填状态。
    const isGroupReview = searchParams.get('mode') === 'group-review';
    // confirm body 里的 providerId：代填时用目标 provider，否则用当前登录用户
    const providerId: number | undefined = overrideProviderId ? Number(overrideProviderId) : getUser('data')?.id;

    // 入口分叉：先选择 solo / group，再进入表单向导。代填模式默认 group，跳过入口。
    const [role, setRole] = useState<TCredRole | null>(isOverrideMode ? 'group' : null);
    const [currentStep, setCurrentStep] = useState(0);
    // 从 overview 判断是否已有进度以决定「续填到第几步」；判定完成前不渲染入口，避免闪现后跳转
    const [checkingResume, setCheckingResume] = useState(!isOverrideMode && !isGroupReview);
    const resumeHandledRef = useRef(false);
    // 后端是否已存过计费 NPI 类型。仅第一次选择走通用 record 保存；此后任何重选
    // （向导第一步 Back 回入口 / Review 页「Change billing type」/ group 带 ?repick=1 跳回 / 续填后重选）
    // 都须走 switch-billing-type 接口，由后端处理切换副作用
    const npiPickedRef = useRef(false);
    // 切换接口进行中：入口卡片防重复点击
    const [switchingType, setSwitchingType] = useState(false);

    // 代填模式最后一步的 payer 选择弹窗（ProviderPayerModal 内部按 providerId 拉 states 再拉 payers）
    const [payerModalOpen, setPayerModalOpen] = useState(false);
    // 代填模式下从 credentialing overview 里按 providerId 取该 provider 的姓名，供弹窗文案使用
    const [credentialInfo] = useCredentialInfo();
    const overrideProvider = useMemo(
        () => credentialInfo.providers.find((p) => p.providerId === Number(providerId)),
        [credentialInfo.providers, providerId],
    );
    // firstName/lastName can be null in the overview data; filter them out so the copy never shows "null null".
    const providerName =
        [overrideProvider?.firstName, overrideProvider?.lastName].filter(Boolean).join(' ').trim() || 'this provider';
    // 向导走完后进入 Review 页；提交若有缺失则展开对应 section 并提示
    const [reviewMode, setReviewMode] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [editingSections, setEditingSections] = useState<CredStepKey[]>([]);
    const [savingSection, setSavingSection] = useState<CredStepKey | null>(null);
    const [addPayersOpen, setAddPayersOpen] = useState(false);
    // 用于在 StepPractice 挂载回填「Same as my contact address」后强制父组件重算 values / 横幅 / 徽标
    const [, forceRefresh] = useState(0);
    // Cancel 内联编辑时用于回滚的整表快照
    const snapshotRef = useRef<Record<string, any> | null>(null);

    // 滚动容器是固定定位的 wizardPage（不是 window），需滚动它本身回到顶部
    const wizardRef = useRef<HTMLDivElement>(null);
    const scrollTop = () => wizardRef.current?.scrollTo({ top: 0 });

    // 已从后端拉取过数据的步骤（避免重复拉取覆盖用户编辑）
    const loadedRef = useRef<Set<CredStepKey>>(new Set());
    // 正在拉取初次数据的步骤：加载期间禁用 Next，避免用空表单 saveStep 覆盖后端数据
    const [loadingStep, setLoadingStep] = useState<CredStepKey | null>(null);
    const [reviewLoading, setReviewLoading] = useState(false);
    // Next 点击后的保存进行中：按钮转圈并防重复点击，保存失败不进入下一步
    const [savingNext, setSavingNext] = useState(false);

    // group 的执业开始日期和 D0 两题（10/26）是 practice 级的值，但按 provider 各存一份，后端不做回退：
    // 本 provider 还没填的字段，先取 owner（登录用户，只有 owner / manager 走 group 流程）的，
    // owner 自己不是 provider 没填过时，再按名单顺序取第一个已填的；只补空着的字段
    const prefillPracticeShared = async () => {
        if (credentialInfo.billingNpiType?.value !== ENPIType.PRACTICE_NPI) return;
        const loginId = getUser('data')?.id;
        const candidates = [
            loginId,
            ...credentialInfo.providers.map((p) => p.providerId).filter((id) => id !== loginId),
        ].filter((id) => id && id !== Number(providerId));
        let missing = PRACTICE_SHARED_FIELDS.filter((f) => !form.getFieldValue(f));
        // eslint-disable-next-line no-restricted-syntax
        for (const id of candidates) {
            if (!missing.length) return;
            try {
                // eslint-disable-next-line no-await-in-loop
                const r: any = await getCredPractice(id);
                const dto = r?.data?.data || {};
                missing.forEach((f) => {
                    if (dto[f] && !form.getFieldValue(f)) form.setFieldValue(f, hydrateDates(dto[f]));
                });
                missing = missing.filter((f) => !form.getFieldValue(f));
            } catch (e) {
                // 忽略：取不到就让用户自己填
            }
        }
    };

    // 进入某步骤时拉取后端已保存数据回填（best-effort，接口不通不阻塞流程）
    const loadStep = async (key: CredStepKey) => {
        if (loadedRef.current.has(key)) return;
        loadedRef.current.add(key);
        setLoadingStep(key);
        try {
            const res: any = await STEP_API[key].get(overrideProviderId);
            const dto = res?.data?.data;
            if (dto != null) form.setFieldsValue(hydrateDates(stepDtoToPatch(key, dto)));
            if (key === 'practice' && PRACTICE_SHARED_FIELDS.some((f) => !dto?.[f])) await prefillPracticeShared();
        } catch (e) {
            // 忽略：接口未就绪/未登录时保持本地表单
        } finally {
            setLoadingStep((cur) => (cur === key ? null : cur));
        }
    };

    // 保存当前步骤：只取本步骤对应的接口字段（表单字段即 DTO），序列化日期后 POST。
    // Request 层失败不 throw 而是返回 { error }（且已 toast 后端信息），这里转成布尔给调用方决定是否拦截
    const saveStep = async (key: CredStepKey) => {
        try {
            return await saveStepValues(key, form, overrideProviderId);
        } catch (e) {
            return false;
        }
    };

    const step = STEPS[currentStep];
    const isLast = currentStep === STEPS.length - 1;
    const isEntry = role === null;
    // 当前步骤初次数据仍在加载：禁用 Next，防止把未回填的空表单保存上去覆盖后端数据
    const isStepLoading = !isEntry && !reviewMode && loadingStep === CRED_STEP_KEYS[currentStep];

    const values = reviewMode ? form.getFieldsValue(true) : {};
    const reviewErrors = reviewMode ? getReviewErrors(values) : [];
    const showBanner = submitAttempted && reviewErrors.length > 0;

    // 挂载时拉取基础枚举（Credentials 等下拉的数据源）
    useEffect(() => {
        loadBasicData();
    }, [loadBasicData]);

    // 每一步（含 Review）对应一个 AI 入口 pageId；与 CRED_STEP_KEYS 顺序对齐
    const aiPageId = useMemo(() => {
        if (reviewMode) return EAIChatPageId.CREDENTIALING_REVIEW;
        const stepPageIds = [
            EAIChatPageId.CREDENTIALING_BASIC_INFO,
            EAIChatPageId.CREDENTIALING_LICENSES,
            EAIChatPageId.CREDENTIALING_PRACTICE,
            EAIChatPageId.CREDENTIALING_MALPRACTICE,
            EAIChatPageId.CREDENTIALING_BACKGROUND,
            EAIChatPageId.CREDENTIALING_REFERENCES,
            EAIChatPageId.CREDENTIALING_VERIFICATION,
        ];
        return stepPageIds[currentStep] || EAIChatPageId.CREDENTIALING_BASIC_INFO;
    }, [reviewMode, currentStep]);

    // 进入向导某步骤时拉取后端数据
    useEffect(() => {
        if (!isEntry && !reviewMode) loadStep(CRED_STEP_KEYS[currentStep]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEntry, reviewMode, currentStep]);

    // 进入 Review 时补齐没走过的步骤（续填直接跳到中间步骤时前面几步表单为空）；
    // 只补 loadedRef 里没有的，避免覆盖用户改了还没保存的内容
    useEffect(() => {
        if (isEntry || !reviewMode || isGroupReview) return undefined;
        const missing = CRED_STEP_KEYS.filter((key) => !loadedRef.current.has(key));
        if (missing.length === 0) return undefined;
        missing.forEach((key) => loadedRef.current.add(key));
        let cancelled = false;
        setReviewLoading(true);
        (async () => {
            const patches = await Promise.all(
                missing.map(async (key) => {
                    try {
                        const res: any = await STEP_API[key].get(overrideProviderId);
                        return stepDtoToPatch(key, res?.data?.data);
                    } catch (e) {
                        return {};
                    }
                }),
            );
            if (cancelled) return;
            form.setFieldsValue(hydrateDates(Object.assign({}, ...patches)));
            setReviewLoading(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEntry, reviewMode]);

    // Review 页「Change billing type」带 ?repick=1 进来：置位重选标记并强制停在入口选择页
    // （否则已存的 billingNpiType 会把用户弹回向导/group 流程）。
    useEffect(() => {
        if (!searchParams.get('repick')) return;
        npiPickedRef.current = true;
        resumeHandledRef.current = true;
        setCheckingResume(false);
        setSearchParams(
            (prev) => {
                prev.delete('repick');
                prev.delete('type');
                return prev;
            },
            { replace: true },
        );
    }, [searchParams, setSearchParams]);

    // 续填：拉一次 overview，若已保存过计费 NPI 类型说明此前已进入流程，
    // 直接跳过入口选择，按 billingNpiType.value（PRACTICE_NPI / INDIVIDUAL_NPI）与 onboardingStep 定位到对应步骤。
    // 仅在非代填、非 group-review 的常规入口下生效，只在挂载时执行一次。
    useEffect(() => {
        if (isOverrideMode || isGroupReview) {
            setCheckingResume(false);
            return;
        }
        if (resumeHandledRef.current) return;
        resumeHandledRef.current = true;
        let cancelled = false;
        (async () => {
            let overview: TCredentialingOverview | undefined;
            try {
                const res: any = await getCredentialingOverview();
                overview = res?.data?.data;
            } catch (e) {
                // 忽略：接口未就绪时退回入口选择
            }
            if (cancelled) return;
            const npi = overview?.billingNpiType?.value;
            // 已存过类型：之后在入口的任何重选都算切换，走 switch 接口
            if (npi) npiPickedRef.current = true;
            // Practice NPI（group）续填走独立的 group credentialing 流程
            // if (npi === ENPIType.PRACTICE_NPI) {
            //     navigate(PATH.GROUP_CREDENTIALING, { replace: true });
            //     return;
            // }
            // Individual NPI（solo）续填：写回 type，跳过入口，定位到上次所在步骤
            if (npi === ENPIType.INDIVIDUAL_NPI) {
                const provider = overview?.providers?.[0];
                // 已提交（COMPLETED）：不再回向导，直接去 submitted 确认页
                if (provider?.onboardingStatus === 'COMPLETED') {
                    navigate(PATH.CREDENTIALING_SUBMITTED, { replace: true });
                    return;
                }
                setSearchParams(
                    (prev) => {
                        prev.set('type', ENPIType.INDIVIDUAL_NPI);
                        return prev;
                    },
                    { replace: true },
                );
                setRole('solo');
                setCurrentStep(resumeStepFromToken(provider?.onboardingStep));
                scrollTop();
            }
            // 未选择过 npi：停留在入口选择页
            setCheckingResume(false);
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSelectRole = async (selected: TCredRole) => {
        if (switchingType) return;
        trackHeapEvent('WebApp - PPA Credentialing Start - Click - Select Role', { role: selected });
        const targetType = selected === 'group' ? ENPIType.PRACTICE_NPI : ENPIType.INDIVIDUAL_NPI;
        if (npiPickedRef.current) {
            // 已选过类型后的重选（Back 回入口 / Review 改类型等）：调 switch-billing-type，
            // 由后端处理切换副作用（Group→Solo 会移除组内其它 provider 与 practice 行）。有数据变更，须等成功再继续
            setSwitchingType(true);
            try {
                const res: any = await switchCredBillingType(targetType);
                if (res?.error) throw new Error(res.error);
            } catch (e) {
                message.error('Failed to change billing type. Please try again.');
                return;
            } finally {
                setSwitchingType(false);
            }
        } else {
            // 首次选择：记录计费 NPI 类型（solo=INDIVIDUAL_NPI / group=PRACTICE_NPI）；best-effort，不阻塞导航
            saveCredBillingNpiType(targetType);
            npiPickedRef.current = true;
        }
        // Practice NPI（group）走独立的 group credentialing 流程
        if (selected === 'group') {
            navigate(PATH.GROUP_CREDENTIALING);
            return;
        }
        // Individual NPI：留在 start 页，把 type 写进 URL（供 StepPractice 回填 Practice type）；
        // 从 group review「Change billing type」带进来的 repick 参数此时已完成使命，一并清掉
        setSearchParams(
            (prev) => {
                prev.set('type', ENPIType.INDIVIDUAL_NPI);
                prev.delete('repick');
                return prev;
            },
            { replace: true },
        );
        setRole(selected);
        setCurrentStep(0);
        scrollTop();
    };

    // Review 页 Billing type 卡「Change billing type」：回到入口选择页重新选 NPI 类型。
    // 本来就在 start 页，直接重置本地状态即可（resumeHandledRef 已置位，续填 effect 不会再弹回向导）；
    // 此处不动后端数据，真正的切换在入口重选时由 switch-billing-type 接口完成（见 handleSelectRole）
    const handleRepickBillingType = () => {
        trackHeapEvent('WebApp - PPA Credentialing Start - Click - Change Billing Type', {});
        npiPickedRef.current = true;
        setReviewMode(false);
        setEditingSections([]);
        setSubmitAttempted(false);
        setCurrentStep(0);
        setRole(null);
        setSearchParams(
            (prev) => {
                prev.delete('type');
                return prev;
            },
            { replace: true },
        );
        scrollTop();
    };

    const handleExit = () => {
        trackHeapEvent('WebApp - PPA Credentialing Start - Click - Save And Exit', {
            step: isEntry ? 'entry' : currentStep,
        });
        // 保存当前步骤后退出（best-effort），回落地页并提示「进度已保存」
        if (!isEntry && !reviewMode) saveStep(CRED_STEP_KEYS[currentStep]);
        navigate(PATH.CREDENTIALING_LANDING, { state: { savedExit: true } });
    };

    const handleBack = () => {
        if (reviewMode) {
            setReviewMode(false);
            setEditingSections([]);
            scrollTop();
            return;
        }
        if (isEntry) {
            navigate(PATH.CREDENTIALING_LANDING);
            return;
        }
        if (currentStep > 0) {
            setCurrentStep(currentStep - 1);
            scrollTop();
            return;
        }
        // 代填模式没有入口选择页，第一步返回直接回上一页（group add-provider）
        if (isOverrideMode) {
            navigate(-1);
            return;
        }
        // 向导第一步返回：回到入口选择
        setRole(null);
        scrollTop();
    };

    const handleNext = async () => {
        // 初次数据仍在加载或上一次保存尚未返回时不响应，避免 saveStep 把空表单写回覆盖后端数据/重复提交
        if (isStepLoading || savingNext) return;
        trackHeapEvent('WebApp - PPA Credentialing Start - Click - Next', { step: currentStep, key: step.key });

        // Basic information 步 Next 拦截：NPI 填了就必须在 NPI Registry 查得到、是个人 NPI（Type 1）、
        // 且登记姓名与所填姓名一致。查询中（含防抖等待）同样拦住，避免用未定的状态往下走。
        // 错误文案由 NpiRegistryBox 在字段下方内联展示，这里只 toast + 滚动定位，不重复标红
        if (CRED_STEP_KEYS[currentStep] === 'identity') {
            const values2 = form.getFieldsValue(true);
            const npiState = npiIssue(values2, {
                expectType: 'Type 1',
                expectedName: { firstName: values2.firstName, lastName: values2.lastName },
            });
            if (npiState) {
                form.scrollToField(['npi'], { behavior: 'smooth', block: 'center' });
                message.error(npiIssueText(npiState, 'Type 1'));
                return;
            }
        }

        // Licenses 步 Next 拦截：至少一条 practice license、已填的行须补全（ID 两项留给 Review 校验）。
        // 校验失败不保存也不导航（用户主动 Save and exit 时才会存草稿）
        if (CRED_STEP_KEYS[currentStep] === 'licenses') {
            const { fields: missing, noLicenseRow } = getLicensesStepValidation(form.getFieldsValue(true));
            if (missing.length > 0 || noLicenseRow) {
                if (noLicenseRow) {
                    message.error('Please add at least one practice license.');
                } else {
                    form.setFields(missing);
                    form.scrollToField(missing[0].name, { behavior: 'smooth', block: 'center' });
                    message.error('Please complete the required fields before continuing.');
                }
                return;
            }
        }

        // 保存当前步骤：失败时留在本步（Request 层已 toast 后端错误信息）
        setSavingNext(true);
        const saved = await saveStep(CRED_STEP_KEYS[currentStep]);
        setSavingNext(false);
        if (!saved) return;

        // 其余中间步骤不做校验拦截，直接进入下一步
        if (!isLast) {
            setCurrentStep(currentStep + 1);
            scrollTop();
            return;
        }
        // 代填模式（group owner 代填 provider）最后一步 Next：弹 payer 选择弹窗，不进 Review
        if (isOverrideMode) {
            setPayerModalOpen(true);
            return;
        }
        // 最后一步 Next：进入 Review 页（不直接提交）
        setReviewMode(true);
        scrollTop();
    };

    const openSectionEdit = (key: CredStepKey) => {
        snapshotRef.current = form.getFieldsValue(true);
        setEditingSections((prev) => (prev.includes(key) ? prev : [...prev, key]));
    };

    const cancelSectionEdit = (key: CredStepKey) => {
        if (snapshotRef.current) form.setFieldsValue(snapshotRef.current);
        setEditingSections((prev) => prev.filter((k) => k !== key));
    };

    const saveSectionEdit = async (key: CredStepKey) => {
        // 先按该 section 规则校验缺失/格式，未通过则标红定位不提交
        const current = form.getFieldsValue(true);
        const missing = getSectionMissingFields(current, key);
        if (missing.length > 0) {
            // 标红剔除 NpiRegistryBox 已内联展示的错误，避免同一条错误上下两行红字
            form.setFields(fieldMarkableErrors(missing));
            form.scrollToField(missing[0].name, { behavior: 'smooth', block: 'center' });
            message.error('Please complete the required fields before saving.');
            return;
        }
        setSavingSection(key);
        try {
            const ok = await saveStepValues(key, form, overrideProviderId);
            if (!ok) throw new Error('save failed');
            snapshotRef.current = form.getFieldsValue(true);
            setEditingSections((prev) => prev.filter((k) => k !== key));
            message.success('Changes saved.');
        } catch (e) {
            message.error('Failed to save changes. Please try again.');
        } finally {
            setSavingSection((cur) => (cur === key ? null : cur));
        }
    };

    const handleReviewSubmit = async () => {
        const current = form.getFieldsValue(true);
        const errors = getReviewErrors(current);

        if (errors.length) {
            setSubmitAttempted(true);
            // 展开所有有缺失的 section，滚动到第一个，并触发内联校验高亮
            const incomplete = REVIEW_SECTIONS.filter((sec) => getSectionErrors(current, sec.key).length).map(
                (sec) => sec.key,
            );
            snapshotRef.current = current;
            setEditingSections(incomplete);
            scrollTop();
            // 展开后字段才挂载，下一帧再标红缺失项并滚动到第一个 section。
            // 注意：StepPractice 挂载后会用「Same as my contact address」把联系地址回填进 addressList[0]，
            // 所以要用回填之后的最新表单值重新计算，避免把已回填的字段误标成 required（陈旧快照会导致有值却报必填）。
            setTimeout(() => {
                const fresh = form.getFieldsValue(true);
                incomplete.forEach((key) => form.setFields(fieldMarkableErrors(getSectionMissingFields(fresh, key))));
                // 回填已写入表单，强制重算顶部横幅 / section 徽标，使其与内联标红一致
                forceRefresh((n) => n + 1);
                const first = document.querySelector(`[data-section="${incomplete[0]}"]`);
                if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 60);
            return;
        }
        trackHeapEvent('WebApp - PPA Credentialing Start - Submit - Application', {});

        // 校验通过后进入 payer 选择 + 付款弹窗；弹窗流程走完（onEnd）再跳 submitted 确认页
        // 「Application submitted」提示放到弹窗流程真正完成后，这一步先不弹绿色 toast
        // 注：CAQH 选「No」的拦截放在 Next（Verification 步）与 Save changes（内联编辑）两处，这里不再重复弹窗
        setAddPayersOpen(true);
    };

    const stepsNav = useMemo(
        () =>
            STEPS.map((st, i) => (
                <div
                    key={st.key}
                    className={cx(s.stepItem, i === currentStep && s.active, i < currentStep && s.completed)}
                >
                    <div className={s.stepBar} />
                    <div className={s.stepNum}>Step {i + 1}</div>
                    <div className={s.stepName}>{st.title}</div>
                </div>
            )),
        [currentStep],
    );

    const renderEntry = () => (
        <div className={s.entryWrap}>
            <div className={s.wizTitle}>What best describes you?</div>
            <div className={s.wizSubtitle}>This sets which NPI and tax ID your claims are billed under.</div>
            <div className={s.entryCards}>
                <div
                    className={cx(s.entryCard, switchingType && s.entryCardDisabled)}
                    role="button"
                    tabIndex={0}
                    aria-disabled={switchingType}
                    onClick={() => handleSelectRole('group')}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectRole('group');
                        }
                    }}
                >
                    <span className={s.entryCardBody}>
                        <span className={s.entryCardTitleRow}>
                            <span className={s.entryCardTitle}>Practice NPI</span>
                            <span className={s.entryRecTag}>Recommended</span>
                        </span>
                        <span className={s.entryCardDesc}>
                            Your claims are billed under your practice&apos;s group NPI and EIN, not your personal SSN.
                            It keeps billing under your business and lets you add providers anytime. You can choose this
                            even as a one-person practice.
                        </span>
                    </span>
                </div>
                <div
                    className={cx(s.entryCard, switchingType && s.entryCardDisabled)}
                    role="button"
                    tabIndex={0}
                    aria-disabled={switchingType}
                    onClick={() => handleSelectRole('solo')}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleSelectRole('solo');
                        }
                    }}
                >
                    <span className={s.entryCardBody}>
                        <span className={s.entryCardTitle}>Individual NPI</span>
                        <span className={s.entryCardDesc}>
                            Your claims are billed under your personal NPI and SSN (or individual Tax ID). Choose this
                            if you practice fully on your own, with no practice entity.
                        </span>
                    </span>
                </div>
            </div>
        </div>
    );

    // group review 独立渲染（自包含 chrome/footer），不进入下面的入口/向导/代填结构
    if (isGroupReview) return <GroupReviewView />;

    return (
        <ConfigProvider theme={{ token: { colorPrimary: '#0E62F4' } }}>
            <div className={s.wizardPage} ref={wizardRef}>
                <div className={s.topbar}>
                    <PPAOnboardingHeader showLogout webinarLink={CALENDLY_LINK.KLARITY_BILLING_WEBINAR} />
                </div>

                {/* content + AIChat 组成 flex 行：聊天面板从右侧滑入并挤压正文（与 PPAOnboardingSteps 一致），
                    避免面板落到页面底部、按钮压到 footer 上 */}
                <div className={s.main}>
                    <div className={s.mainCol}>
                        {/* 续填判定期间不渲染入口，避免入口闪现后再跳转/续填 */}
                        {isEntry && !checkingResume && renderEntry()}

                        {!isEntry && reviewMode && (
                            <>
                                <div className={s.wizHeader}>
                                    <div className={s.wizTitle}>Review your credentialing before you submit</div>
                                    <div className={s.wizSubtitle}>
                                        Please confirm your information before submitting. You can always edit later if
                                        something changes.
                                    </div>
                                </div>
                                <CredTargetProviderContext.Provider value={overrideProviderId}>
                                    <Form form={form} layout="vertical" requiredMark={false} className={s.wizBody}>
                                        {showBanner && (
                                            <div className={s.reviewErrorBanner}>
                                                <BannerIcon />
                                                <div>
                                                    <div className={s.reviewErrorTitle}>
                                                        Please complete the following before submitting
                                                    </div>
                                                    <ul>
                                                        {reviewErrors.map((e) => (
                                                            <li key={e}>{e}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            </div>
                                        )}
                                        <BillingTypePanel
                                            variant="solo"
                                            onChangeBillingType={handleRepickBillingType}
                                        />
                                        {REVIEW_SECTIONS.map((sec) => (
                                            <ReviewSection
                                                key={sec.key}
                                                sectionKey={sec.key}
                                                title={sec.title}
                                                values={values}
                                                editing={editingSections.includes(sec.key)}
                                                saving={savingSection === sec.key}
                                                footerOffset
                                                hasErrors={
                                                    submitAttempted && getSectionErrors(values, sec.key).length > 0
                                                }
                                                onEdit={() => openSectionEdit(sec.key)}
                                                onCancel={() => cancelSectionEdit(sec.key)}
                                                onSave={() => saveSectionEdit(sec.key)}
                                            >
                                                {renderCredStep(sec.key)}
                                            </ReviewSection>
                                        ))}
                                    </Form>
                                </CredTargetProviderContext.Provider>
                            </>
                        )}

                        {!isEntry && !reviewMode && (
                            <>
                                <div className={s.stepsNav}>{stepsNav}</div>

                                <div className={s.wizHeader}>
                                    <div className={s.wizTitle}>{step.title}</div>
                                    <div className={s.wizSubtitle}>{step.sub}</div>
                                </div>

                                <CredTargetProviderContext.Provider value={overrideProviderId}>
                                    <Form
                                        form={form}
                                        layout="vertical"
                                        requiredMark={false}
                                        className={s.wizBody}
                                        scrollToFirstError
                                    >
                                        {renderCredStep(CRED_STEP_KEYS[currentStep])}
                                    </Form>
                                </CredTargetProviderContext.Provider>
                            </>
                        )}
                    </div>

                    {/* 每一步的 AI 入口（入口选择页不展示）；作为 flex 行的右列，展开时从右侧滑入 */}
                    {!isEntry && (
                        <div className={s.chatCol}>
                            <AIChat pageId={aiPageId} defaultShow={false} buttonStyle={{ bottom: '100px' }} />
                        </div>
                    )}
                </div>

                <div className={s.footer}>
                    <div className={s.footerInner}>
                        <button type="button" className={s.exitBtn} onClick={handleExit}>
                            Save and exit
                        </button>
                        <div className={s.footerRight}>
                            <button type="button" className={s.backBtn} onClick={handleBack}>
                                Back
                            </button>
                            {!isEntry &&
                                (reviewMode ? (
                                    <Button
                                        type="primary"
                                        size="large"
                                        className={s.nextBtn}
                                        onClick={handleReviewSubmit}
                                        loading={reviewLoading}
                                        // 有 section 正在内联编辑时禁止提交（须先 Save changes / Cancel），与其它页面一致
                                        disabled={editingSections.length > 0 || reviewLoading}
                                    >
                                        Submit application
                                    </Button>
                                ) : (
                                    <Button
                                        type="primary"
                                        size="large"
                                        className={s.nextBtn}
                                        onClick={handleNext}
                                        loading={isStepLoading || savingNext}
                                    >
                                        Next
                                    </Button>
                                ))}
                        </div>
                    </div>
                </div>

                {/* 代填模式最后一步的两步 payer 选择弹窗；完成后回到上一页（group add-provider）*/}
                <ProviderPayerModal
                    open={payerModalOpen}
                    providerId={Number(providerId)}
                    providerName={providerName}
                    onClose={() => setPayerModalOpen(false)}
                    onComplete={() => {
                        setPayerModalOpen(false);
                        navigate(-1);
                    }}
                />
                <CredentialingModal
                    open={addPayersOpen}
                    onClose={() => setAddPayersOpen(false)}
                    flowMode={ECredentialingFlowMode.INITIAL}
                    targetProviderId={Number(providerId)}
                    // Service address 步骤的预填源：Basic information 的 contactAddress，仅作用于同州的州块
                    addressPrefill={(() => {
                        const addr = form.getFieldValue('contactAddress') || {};
                        return {
                            street: addr.practiceAddress,
                            city: addr.practiceCity,
                            zip: addr.practiceZip,
                            state: addr.practiceState,
                        };
                    })()}
                    onEnd={() => navigate(PATH.CREDENTIALING_SUBMITTED)}
                />
            </div>
        </ConfigProvider>
    );
}
